import { localNumberFormat } from "utils";

import { CM_ROUNDING_STEP, CURRENCY_CODE, CurrencyCode } from "consts";

type SignDisplay = "auto" | "always" | "exceptZero" | "never";
type CurrencyDisplay = NonNullable<Intl.NumberFormatOptions["currencyDisplay"]>;

export interface FormatCurrencyOptions {
  currency?: CurrencyCode;
  decimals?: number;
  signDisplay?: SignDisplay;
  currencyDisplay?: CurrencyDisplay;
}

// A double carries ~15-17 significant decimal digits, so rounding to 12 discards the representation
// error a decimal fraction picks up when scaled, while leaving every digit a real value can hold.
const SIGNIFICANT_DIGITS_BEFORE_FLOAT_NOISE = 12;

export const sumFloatingPointNumbers = (a: number, b: number) => trimToDecimals(a + b, 6);

export const sumNumbers = (nums: number[]) => nums.reduce(sumFloatingPointNumbers, 0);

export const getCurrencySymbol = (currency: CurrencyCode = CURRENCY_CODE.EUR): string =>
  new Intl.NumberFormat(localNumberFormat(), { style: "currency", currency, currencyDisplay: "symbol" })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value ?? currency;

export const formatNumericCurrency = (
  number: number,
  { currency = CURRENCY_CODE.EUR, decimals = 2, signDisplay = "auto", currencyDisplay = "symbol" }: FormatCurrencyOptions = {}
) =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency,
    currencyDisplay,
    signDisplay,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);

/**
 * Single source of truth for the "no allotment" payout fallback: renders "€–" instead of "€0"
 * so every surface (impact panel, field cell, field details) agrees on when a farm has no allotment.
 */
export const formatPayoutOrDash = (value: number, hasAllotment: boolean, currency?: CurrencyCode): string =>
  hasAllotment ? formatNumericCurrency(value, { decimals: 0, currency }) : `${getCurrencySymbol(currency)}–`;

/** SoilSight CM outcome display: nearest CM_ROUNDING_STEP, half-up at ties. */
export const roundCmOutcome = (value: number): number => Math.round(value / CM_ROUNDING_STEP) * CM_ROUNDING_STEP || 0;

export const formatCmOutcome = (value: number, opts: FormatCurrencyOptions = {}): string =>
  formatNumericCurrency(roundCmOutcome(value), { decimals: 0, ...opts });

/**
 * @deprecated Use `formatNumericCurrency` instead.
 */
export const formatCurrency = (number: number, decimals = 2, signDisplay: SignDisplay = "auto") =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency: "EUR",
    currencyDisplay: "symbol",
    signDisplay,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);

export const formatCurrencyForAdmin = (number: number, signDisplay: SignDisplay = "auto") =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency: "EUR",
    currencyDisplay: "symbol",
    signDisplay,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);

export const formatCurrencyRange = (min: number, max: number, options: FormatCurrencyOptions = {}) => {
  if (min === max) {
    return formatNumericCurrency(min, options);
  }

  return `${formatNumericCurrency(min, options)} \u2014 ${formatNumericCurrency(max, options)}`;
};

export const formatNumber = ({ number, decimalPlaces = 2, locale }: { number: number; decimalPlaces?: number; locale?: string }) =>
  new Intl.NumberFormat(locale ?? localNumberFormat(), {
    style: "decimal",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(number);

export const formatCO2Amount = (numberOrString: number | string, decimals = 2) => {
  const number = typeof numberOrString === "number" ? numberOrString : parseFloat(numberOrString);

  const formatter = new Intl.NumberFormat(localNumberFormat(), {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${formatter.format(number / 1000)}t`;
};

export const formatCO2AmountRange = (min: number, max: number, decimals = 2) => {
  if (min === max) {
    return formatCO2Amount(min, decimals);
  }

  return `${formatCO2Amount(min, decimals)} - ${formatCO2Amount(max, decimals)}`;
};

export const getVariance = (number1: string | number, number2: number, decimals: number) => {
  const parsedNumber1 = typeof number1 === "string" ? parseFloat(number1) : number1;

  formatNumber({
    number: ((number2 - parsedNumber1) / parsedNumber1) * 100,
    decimalPlaces: decimals,
  });
};

export const generateRandomNumber = (min: number, max: number) => Math.floor(Math.random() * max) + min;

export const truncateCredits = (amount: number) => Math.trunc(amount * 1000) / 1000;

export const normalise = (val: number, max: number, min: number) => (val - min) / (max - min);

export const formatPercentage = (v: number) => parseFloat(v.toFixed(2)) / 100;

/**
 * @function fractionToPercentage
 * @description Converts a fraction into the percentage a user should see. Percentages are stored
 *  0-1 across the platform, and a bare `fraction * 100` leaks binary floating point error straight
 *  into the UI (0.07 * 100 === 7.000000000000001), so the product is rounded back to the significant
 *  digits a decimal fraction can genuinely carry. Values below that noise floor are preserved.
 * @param fraction - The stored fraction, e.g. 0.2
 * @returns The equivalent percentage, e.g. 20
 * @example fractionToPercentage(0.2) => 20
 * @example fractionToPercentage(0.07) => 7
 * @example fractionToPercentage(0.005) => 0.5
 */
export const fractionToPercentage = (fraction: number) => Number((fraction * 100).toPrecision(SIGNIFICANT_DIGITS_BEFORE_FLOAT_NOISE));

/**
 * @function isPercentageComplete
 * @description Check if a value is "100%."  We provide a bit of wiggle room
 *  (>= 0.99) here to account for floating point precision issues.
 * @param v - The value to check
 * @returns If v is "100%"
 */
export const isPercentageComplete = (v: number) => v >= 0.99 && v <= 1.001;

export const trimToDecimals = (value: number, decimals: number = 2): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

/**
 * @function weightedAverage
 * @description Weighted mean of `value(row)` across `rows`, weighted by `weight(row)`.
 * @param rows - The rows to average over
 * @param weight - Reads each row's weight, e.g. its dry mass
 * @param value - Reads the value being averaged, e.g. its emission factor
 * @returns The weighted mean, or 0 when the total weight is not positive
 * @example weightedAverage([{ kg: 2, ef: 1 }, { kg: 1, ef: 4 }], (r) => r.kg, (r) => r.ef) => 2
 */
export const weightedAverage = <T>(rows: T[], weight: (_row: T) => number, value: (_row: T) => number): number => {
  const totalWeight = rows.reduce((sum, row) => sum + weight(row), 0);
  if (totalWeight <= 0) return 0;

  return rows.reduce((sum, row) => sum + weight(row) * value(row), 0) / totalWeight;
};

/**
 * @function getYearArray
 * @description Generates an array of years from startYear to endYear (inclusive).
 * @example getYearArray(2020, 2023) => [2020, 2021, 2022, 2023]
 * @param startYear Year to start counting from
 * @param endYear Year to end counting at
 * @returns An array of years (inclusive) from startYear to endYear
 */
export function getYearArray(startYear: number, endYear: number) {
  const years = [];
  for (let i = startYear; i <= endYear; i += 1) {
    years.push(i);
  }
  return years;
}
